import { Injectable } from '@angular/core';
import {
  Router, Route,
  RouteConfigLoadEnd, RouteConfigLoadStart
} from '@angular/router';
import { filter } from 'rxjs/operators';

type FlatRoute = {
  fullPath: string;           // /parent/child/:id
  path?: string;
  component?: string;
  /** Show string redirects, or "(fn)" when it's a redirect function */
  redirectTo?: string | '(fn)';
  outlet?: string;
  pathMatch?: string;
  canActivate?: number;
  loadChildren?: boolean;
  isLazyLoaded?: boolean;
};

@Injectable({ providedIn: 'root' })
export class RoutesDumperService {
  private loadingLazy = 0;

  constructor(private router: Router) {
    this.router.events
      .pipe(filter(e => e instanceof RouteConfigLoadStart || e instanceof RouteConfigLoadEnd))
      .subscribe(e => {
        this.loadingLazy += e instanceof RouteConfigLoadStart ? 1 : -1;
        if (this.loadingLazy === 0) {
          //console.log('All lazy routes loaded');
          this.dumpToConsole();
        }
      });
  }

  /** Call once after app start (dev only) */
  dumpToConsole(convertParamsToBraces = false): FlatRoute[] {
    const flat = this.flatten(this.router.config);

    const rows = flat
      .map(r => {
        // FIX 1: avoid duplicate 'fullPath' by destructuring
        const { fullPath, ...rest } = r;
        return {
          ...rest,
          fullPath: convertParamsToBraces ? this.toBraced(fullPath) : fullPath
        };
      })
      // de-dupe + sort
      .filter((v, i, a) => a.findIndex(t => t.fullPath === v.fullPath) === i)
      .sort((a, b) => a.fullPath.localeCompare(b.fullPath));

    //console.table(rows);
    //const json = JSON.stringify(rows.map(r => r.fullPath), null, 2);
    //if (navigator?.clipboard) navigator.clipboard.writeText(json).catch(() => { });
    //console.log('All route paths copied to clipboard as JSON:', json);

    return rows;
  }

  private flatten(routes: Route[], parent = ''): FlatRoute[] {
    const out: FlatRoute[] = [];
    for (const r of routes) {
      const seg = r.path ?? '';
      const fullPath = this.join(parent, seg);

      // FIX 2: redirectTo can be a string OR a function in newer Angular
      const redirectTo =
        typeof r.redirectTo === 'string'
          ? r.redirectTo
          : r.redirectTo
            ? '(fn)'
            : undefined;

      out.push({
        fullPath,
        path: r.path,
        component: (r.component as any)?.name,
        redirectTo,
        outlet: (r as any).outlet,
        pathMatch: r.pathMatch,
        canActivate: (r.canActivate || []).length,
        loadChildren: !!r.loadChildren,
        isLazyLoaded: !!(r as any)._loadedConfig || !!(r as any)._loadedRoutes
      });

      if (r.children?.length) out.push(...this.flatten(r.children, fullPath));

      // include already-loaded lazy routes
      const loaded = (r as any)._loadedRoutes ?? (r as any)._loadedConfig?.routes;
      if (loaded?.length) out.push(...this.flatten(loaded, fullPath));
    }
    return out;
  }

  private join(parent: string, child: string) {
    const p = parent.replace(/\/+$/, '');
    const c = child.replace(/^\/+/, '');
    if (!p && !c) return '/';
    if (!c) return p || '/';
    var path = `/${[p, c].filter(Boolean).join('/')}`;
    if (path.includes('//')) path = path.replace(/\/+/g, '/');
    return path;
  }

  private toBraced(path: string) {
    return path.replace(/:([A-Za-z0-9_]+)/g, '{$1}');
  }
}
